import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, CalendarDays, Loader2, Check } from "lucide-react";
import { formatDoj, tenureOf, todayLocal } from "@/lib/tenure";

// Designation + Date of joining editor, shared by the onboarding popup and the Timeline.
export const ProfileDetailsForm = ({ user, onSaved, submitLabel = "Save", dark = false, requireAll = false }) => {
    const [designation, setDesignation] = useState(user?.designation || "");
    const [doj, setDoj] = useState(user?.date_of_joining || "");
    const [saving, setSaving] = useState(false);
    const ready = !requireAll || (designation.trim() && doj);

    const save = async (e) => {
        e?.preventDefault();
        if (!ready) return;
        setSaving(true);
        try {
            const r = await api.put(`/users/${user.user_id}`, { designation: designation.trim(), date_of_joining: doj || "" });
            toast.success("Profile details saved");
            await onSaved?.(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save");
        } finally {
            setSaving(false);
        }
    };

    const label = dark ? "text-[11px] uppercase tracking-[0.18em] text-teal-200/70 font-semibold" : "text-xs uppercase tracking-wider text-slate-500 font-semibold";
    const input = dark
        ? "w-full h-12 pl-10 pr-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:border-[#2EB4AC] focus:ring-2 focus:ring-[#2EB4AC]/30 [color-scheme:dark]"
        : "w-full h-11 pl-10 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#2EB4AC] focus:ring-2 focus:ring-teal-100";
    const icon = `absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dark ? "text-[#2EB4AC]" : "text-slate-400"}`;

    return (
        <form onSubmit={save} className="space-y-5" data-testid="profile-details-form">
            <div>
                <label className={label}>Designation</label>
                <div className="relative mt-2">
                    <Briefcase className={icon} />
                    <input
                        value={designation}
                        onChange={(e) => setDesignation(e.target.value)}
                        maxLength={80}
                        placeholder="e.g. Senior Engineer"
                        data-testid="profile-designation-input"
                        className={input}
                    />
                </div>
            </div>
            <div>
                <label className={label}>Date of joining</label>
                <div className="relative mt-2">
                    <CalendarDays className={icon} />
                    <input
                        type="date"
                        value={doj}
                        max={todayLocal()}
                        onChange={(e) => setDoj(e.target.value)}
                        data-testid="profile-doj-input"
                        className={input}
                    />
                </div>
                {doj && (
                    <div className={`mt-2 text-xs ${dark ? "text-white/60" : "text-slate-500"}`} data-testid="profile-doj-preview">
                        Joined {formatDoj(doj)} · <span className={`font-semibold ${dark ? "text-amber-300" : "text-amber-700"}`}>{tenureOf(doj)}</span>
                    </div>
                )}
            </div>
            <button
                type="submit"
                disabled={saving || !ready}
                data-testid="profile-details-save"
                className={`w-full inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-colors disabled:opacity-40 ${dark ? "h-12 bg-[#2EB4AC] hover:bg-[#25908A] text-white shadow-[0_8px_30px_-8px_rgba(46,180,172,0.7)]" : "h-11 bg-[#2EB4AC] hover:bg-[#25908A] text-white"}`}
            >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {submitLabel}
            </button>
        </form>
    );
};
